import { useQuery } from '@apollo/client/react'
import type { TranslationKey } from '@athena/i18n'
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageLayout,
  ResultRow,
  linkClasses,
  useLanguage,
  useTranslate,
} from '@athena/ui'
import { Link } from '@tanstack/react-router'

import { usePageTitle } from '../app/page-title.js'
import { MyHistoryDocument } from '../graphql/generated/operations.js'
import { useFormat } from '../lib/format.js'

/**
 * Why the questions behind a score can no longer be opened, in a student's
 * words.
 *
 * The codes are the server's, and the same ones the detail itself refuses
 * with, so this is a translation rather than a second opinion. A rule change
 * means the server refuses differently and this map needs a line — not some
 * condition here quietly disagreeing.
 */
/**
 * Why the detailed review is closed, keyed by the server's reason code.
 *
 * The server decides *that* it is closed — section 11 keeps that decision
 * server-side — and the dictionary decides how to say so. A code with no entry
 * falls back to "score only" rather than putting the code on screen.
 */
const CLOSED: Record<string, TranslationKey> = {
  PRACTICE_UNASSIGNED: 'history.blockedUnassigned',
  PRACTICE_ARCHIVED: 'history.blockedArchived',
  PRACTICE_CLOSED: 'history.blockedClosed',
  ATTEMPTS_REMAINING: 'history.blockedAttemptsRemaining',
  ATTEMPT_NOT_SUBMITTED: 'history.blockedNotSubmitted',
}

/**
 * Everything this student has finished, for good.
 *
 * A score stays here after the practice leaves their list, is archived, or
 * closes — attempts have never been tied to assignments, so removing one has
 * never removed the other. What goes with the practice is the detail behind
 * the score, and a row says so in words rather than offering a link that
 * would be refused.
 */
export function HistoryScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const format = useFormat()
  usePageTitle(t('history.title'))
  const { data, loading, error } = useQuery(MyHistoryDocument)

  if (loading) {
    return (
      <PageLayout width="operational">
        <LoadingState className="p-0 py-8">{t('common.loading')}</LoadingState>
      </PageLayout>
    )
  }
  if (error) {
    return (
      <PageLayout width="operational">
        <ErrorState className="p-0 py-8">{t('history.loadError')}</ErrorState>
      </PageLayout>
    )
  }

  const history = data?.myHistory ?? []

  if (history.length === 0) {
    return (
      <PageLayout width="operational">
        <EmptyState
          variant="page"
          title={t('history.emptyTitle')}
          description={t('history.emptyDescription')}
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout width="operational">
      <p className="text-body text-text-secondary">{t('history.intro')}</p>

      <ul className="divide-y divide-border">
        {history.map((row) => (
          <li key={row.attemptId}>
            <ResultRow
              title={row.practiceTitle}
              detail={t('history.detail', [format.categoryLine(row), format.date(row.submittedAt)])}
              percent={row.percent}
              correctCount={row.correctCount}
              questionCount={row.questionCount}
              trailing={
                row.canReview ? (
                  <Link
                    to="/$language/learn/attempt/$attemptId"
                    params={{ language, attemptId: row.attemptId }}
                    className={linkClasses({ size: 'sm' })}
                  >
                    {t('history.seeTheAnswers')}
                  </Link>
                ) : (
                  <span className="text-body-sm text-text-secondary">
                    {t(CLOSED[row.reviewBlockedReason ?? ''] ?? 'history.scoreOnly')}
                  </span>
                )
              }
            />
          </li>
        ))}
      </ul>
    </PageLayout>
  )
}
